import GraphicsUpdateArea from "./ui/GraphicsUpdateArea.js";
import NotesUpdateArea from "./ui/NotesUpdateArea.js";
import ResourcesEditorArea from "./ui/ResourcesEditorArea.js";
import InputCoordinatesArea from "./ui/InputCoordinatesArea.js";
import ConfirmationArea from "./ui/ConfirmationArea.js";
import AttachmentsEditorArea from "./ui/AttachmentsEditorArea.js";

import Menu from "./Menu.js";
import Style from "./utils/Style.js";
import RateCap from "./utils/RateCap.js";
import global from "./global.js";

import Renderer from "./scene/Renderer.js";

const d3 = global.d3;
const log = global.log.getLogger("f_mtac.scene");
log.setLevel(log.levels.WARN);

import {format_distance} from "./scene/drivers/Utils.js";
import Line from "./scene/drivers/Line.js";
import CurvedLine from "./scene/drivers/CurvedLine.js";
import Arrow from "./scene/drivers/ArrowLine.js";
import CurvedArrow from "./scene/drivers/CurvedArrowLine.js";
import Disk from "./scene/drivers/Disk.js";
import Polygon from "./scene/drivers/Polygon.js";
import Note from "./scene/drivers/Note.js";
import Attachment from "./scene/drivers/Attachment.js";

import SceneInteractions from "./scene/Interactions.js";

import SceneObjects from "./scene/Objects.js";
import Map from "./scene/Map.js";
import Trace from "./scene/Trace.js";

import Placeholder from "./scene/Placeholder.js";
import Placer from "./scene/Placer.js";
import Indicator from "./scene/Indicator.js";
import ObjectHandle from "./scene/ObjectHandle.js";
import SVGShadow from "./scene/SVGShadow.js";
import Transform from "./scene/Transform.js";
import Tracking from "./scene/Tracking.js";
import {DIRECTION_SOUTH_WEST, DIRECTION_SOUTH_EAST, DIRECTION_NORTH_WEST} from "./scene/Axis.js";

import ModeSwitcher from "./ModeSwitcher.js";
import BottomArea from "./ui/BottomArea.js";
import Floaters from "./ui/Floaters.js";

const Scene = function (view, svg) {
    var context = null;
    var focus_graphicsUpdateArea; // Used to keep in memory the focused graphic
    const style = new Style(); // Also used as common style.rule_set with Graphics and GraphicsUpdateArea to make manipulation of visibility possible through multiples files easily
    const interactions = new SceneInteractions();
    const objects = new SceneObjects(svg);
    objects.layer_create("media", "json");
    objects.layer_create("scene", "svg");
    objects.layer_create("resources", "svg");
    objects.layer_create("notes", "svg");
    objects.layer_create("special", "svg");
    const map = new Map(view, svg, objects);
    const tracking = new Tracking(svg, objects);
    const shadow = new SVGShadow(svg);
    const resources_stack = [],
        objects_stack = [],
        medias_stack = [];
    const colors = [
        {name: "blue", value: "#007bcf"},
        {name: "green", value: "#09d02d"},
        {name: "yellow", value: "#f9f93d"},
        {name: "orange", value: "#ff7f00"},
        {name: "red", value: "#e60000"},
        {name: "black", value: "#000"}
    ];
    const color_by_name = Object.fromEntries(colors.map((color) => [color.name, color]));
    const color_by_value = Object.fromEntries(colors.map((color) => [color.value, color]));

    const floaters = new Floaters();

    this.object_color = (object, color) => {
        let $object_node = $(object.node.node());
        $object_node.find("[stroke]:not(.object_handle_hit_box)").each((_, e) => {
            let $e = $(e);
            if ($e.attr("stroke") !== undefined && $e.attr("stroke") !== "none") {
                $e.attr("stroke", color);
            }
        });
        $object_node.find("[fill]:not(.object_handle_hit_box)").each((_, e) => {
            let $e = $(e);
            if ($e.parent().is("mask")) {
                return;
            }
            if ($e.attr("fill") === undefined || $e.attr("fill") === "none") {
                return;
            }
            $e.attr("fill", color);
        });
        let text_color = "#000";
        if (color === "#000") {
            text_color = "#fff";
        }
        $object_node.find("text").each((_, e) => {
            $(e).css("fill", text_color);
        });
    };

    const scene_mode_switcher = new ModeSwitcher({
        name: "resources_graphics_mode_switcher",
        container_id: "#command_map_right .btn-switcher-right",
        modes: ["map", "graphics", "resources"],
        default_state_name: "neutral",
        states: {
            neutral: {
                in: (_, sm) => {
                    sm.data.selector.resources.addClass("btn-selected");
                },
                out: (_, sm) => {
                    sm.data.selector.resources.removeClass("btn-selected");
                },
                transit: {
                    map: {
                        state: "map",
                        handler: () => true
                    },
                    graphics: {
                        state: "graphics",
                        handler: () => true
                    },
                    resources: {
                        state: "resources",
                        handler: () => true
                    },
                    neutral: {
                        state: "neutral",
                        handler: () => true
                    }
                }
            },
            map: {
                in: (_, sm) => {
                    sm.data.selector.map.addClass("btn-selected");
                    $("#command_map_area").show();
                    svg.layer_insert("geolocation", "axis");
                    if (!map.is_gis) {
                        svg.layer_show("geolocation");
                        global.tools.tools["geo_nodes"].$button.css("display", null);
                        global.tools.tools["map_type"].$button.hide();
                        this.interaction_passive_enable("map_move");
                        global.tools.tools["grid_settings"].$button.css("display", null);
                    } else {
                        svg.layer_hide("geolocation");
                        global.tools.tools["geo_nodes"].$button.hide();
                        global.tools.tools["map_type"].$button.show();
                        global.tools.tools["grid_settings"].$button.show();
                        if (!global.view.is_gis) {
                            global.tools.tools["map_type"].disable();
                        } else {
                            global.tools.tools["map_type"].enable();
                        }
                    }
                    style.rule_set(
                        "map_mode_opacity",
                        `
                        #map_area g.notes {opacity: 0.5;}
                        #map_area g.scene {opacity: 0.5;}
                        #map_area g.resources {opacity: 0.5;}
                    `
                    );
                },
                out: (_, sm) => {
                    sm.data.selector.map.removeClass("btn-selected");
                    $("#command_map_area").css("display", "none");
                    this.interaction_passive_disable("map_move");
                    style.rule_set(
                        "map_mode_opacity",
                        `
                        #map_area g.notes {opacity: 1;}
                        #map_area g.scene {opacity: 1;}
                        #map_area g.resources {opacity: 1;}
                    `
                    );
                    svg.layer_hide("geolocation");
                    if (Menu.currently_opened !== null) {
                        Menu.close(Menu.currently_opened);
                    }
                },
                transit: {
                    graphics: {
                        state: "graphics",
                        handler: () => true
                    },
                    resources: {
                        state: "resources",
                        handler: () => true
                    },
                    neutral: {
                        state: "neutral",
                        handler: () => true
                    }
                },
                schedule: {}
            },
            resources: {
                in: (_, sm) => {
                    sm.data.selector.resources.addClass("btn-selected");
                    $("#command_resources_area").css("display", "block");
                    if ((!(global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_WRITE) && global.storage.shared()) || !map.loaded) {
                        this.interaction_passive_disable("resources_move");
                        global.tools.tools["resource_drop"].disable();
                        global.tools.tools["attachment_drop"].disable();
                    } else {
                        this.interaction_passive_enable("resources_move");
                        global.tools.tools["resource_drop"].enable();
                        global.tools.tools["attachment_drop"].enable();
                    }
                    svg.layer_insert("scene", "axis");
                    svg.layer_insert("notes", "axis");
                    svg.layer_insert("resources", "axis");
                    svg.layer_insert("special", "axis");
                },
                out: (_, sm) => {
                    sm.data.selector.resources.removeClass("btn-selected");
                    $("#command_resources_area").css("display", "none");
                    this.interaction_passive_disable("resources_move");
                    if (Menu.currently_opened !== null) {
                        Menu.close(Menu.currently_opened);
                    }
                },
                transit: {
                    map: {
                        state: "map",
                        handler: () => true
                    },
                    graphics: {
                        state: "graphics",
                        handler: () => true
                    },
                    neutral: {
                        state: "neutral",
                        handler: () => true
                    }
                },
                schedule: {}
            },
            graphics: {
                in: (_, sm) => {
                    sm.data.selector.graphics.addClass("btn-selected");
                    $("#command_graphics_area").css("display", "block");
                    this.interaction_passive_enable("scene_move");
                    svg.layer_insert("resources", "axis");
                    svg.layer_insert("scene", "axis");
                    svg.layer_insert("notes", "axis");
                    svg.layer_insert("special", "axis");
                    style.rule_set(
                        "map_mode_opacity",
                        `
                        #map_area g.notes {opacity: 1;}
                        #map_area g.scene {opacity: 1;}
                        #map_area g.resources {opacity: 0.7;}
                    `
                    );
                },
                out: (_, sm) => {
                    sm.data.selector.graphics.removeClass("btn-selected");
                    $("#command_graphics_area").css("display", "none");
                    this.interaction_passive_disable("scene_move");
                    if (Menu.currently_opened !== null) {
                        Menu.close(Menu.currently_opened);
                    }
                    style.rule_set(
                        "map_mode_opacity",
                        `
                        #map_area g.notes {opacity: 1;}
                        #map_area g.scene {opacity: 1;}
                        #map_area g.resources {opacity: 1;}
                    `
                    );
                },
                transit: {
                    map: {
                        state: "map",
                        handler: () => true
                    },
                    resources: {
                        state: "resources",
                        handler: () => true
                    },
                    neutral: {
                        state: "neutral",
                        handler: () => true
                    }
                },
                schedule: {}
            }
        }
    });

    Object.defineProperty(this, "context", {
        set: () => {},
        get: () => context
    });

    Object.defineProperty(this, "style", {
        set: () => {},
        get: () => style
    });

    Object.defineProperty(this, "objects", {
        set: () => {},
        get: () => objects
    });

    Object.defineProperty(this, "objects_stack", {
        set: () => {},
        get: () => objects_stack
    });

    Object.defineProperty(this, "resources_stack", {
        set: () => {},
        get: () => resources_stack
    });

    Object.defineProperty(this, "medias_stack", {
        set: () => {},
        get: () => medias_stack
    });

    Object.defineProperty(this, "map", {
        set: () => {},
        get: () => map
    });

    Object.defineProperty(this, "tracking", {
        set: () => {},
        get: () => tracking
    });

    Object.defineProperty(this, "mode_switcher", {
        set: () => {},
        get: () => scene_mode_switcher
    });

    Object.defineProperty(this, "shadow", {
        set: () => {},
        get: () => shadow
    });

    Object.defineProperty(this, "colors", {
        set: () => {},
        get: () => colors
    });

    Object.defineProperty(this, "color_by_name", {
        set: () => {},
        get: () => color_by_name
    });

    Object.defineProperty(this, "color_by_value", {
        set: () => {},
        get: () => color_by_value
    });

    Object.defineProperty(this, "interactions", {
        set: () => {},
        get: () => interactions
    });

    Object.defineProperty(this, "floaters", {
        set: () => {},
        get: () => floaters
    });

    this.enable_all_features = function () {
        $("#map_area button.add-media").removeAttr("disabled");
        $("#map_area button.add-gis").removeAttr("disabled");
        global.tools.tools["desktop_settings"].enable();
        if (this.mode_switcher.enable_state) {
            $("#command_area_right .btn-switcher-right .switcher_item_graphics").removeAttr("disabled");
            $("#command_area_right .btn-switcher-right .switcher_item_map").removeAttr("disabled");
            $(".btn-switcher-right").show();
            $("#desktop_tools").css("display", "flex");
            $("#desktop_menu").show();
            if (this.mode_switcher.mode === "graphics") {
                this.interaction_passive_enable("scene_move");
            } else if (this.mode_switcher.mode === "resources") {
                this.interaction_passive_enable("resources_move");
            }
        }
    };
    this.disable_special_features = function () {
        $("#command_area_right .btn-switcher-right .switcher_item_map").attr("disabled", true);
        global.tools.tools["desktop_settings"].disable();
        $("#map_area button.add-media").attr("disabled", true);
        $("#map_area button.add-gis").attr("disabled", true);
    };
    this.disable_write_features = function () {
        $("#command_area_right .btn-switcher-right .switcher_item_graphics").attr("disabled", true);
        this.interaction_passive_disable("resources_move");
        $(".btn-switcher-right").hide();
        $("#desktop_tools").hide();
        $("#desktop_menu").hide();
    };

    const single_tools = [
        "recenter",
        "zoom_plus",
        "zoom_minus",
        "desktop_rename",
        "resource_plus",
        "resource_minus",
        "resource_reset",
        "duplicate_desktop",
        "delete_map",
        "delete_objects",
        "delete_resources",
        "background_image_settings",
        "brightness_minus",
        "brightness_plus",
        "brightness_reset",
        "grid_settings",
        "grid_move",
        "grid_resize",
        "grid_reset",
        "map_type",
        "geocoding"
    ];
    const group_tools = [
        {group: "grid_color_update", tool: "grid_black"},
        {group: "grid_color_update", tool: "grid_white"},
        {group: "grid_color_update", tool: "grid_toggle"}
    ];
    global.event_bus.subscribe("scene.map.state", ({loaded}) => {
        log.debug("Scene.map.state: on loaded", {loaded});
        if (loaded) {
            for (let i in single_tools) {
                global.tools.tools[single_tools[i]].enable();
                // do not enable some tools if tracked unit
                if (tracking.tracked !== undefined) {
                    global.tools.tools["recenter"].disable();
                    global.tools.tools["geocoding"].disable();
                }
            }
            for (let i in group_tools) {
                global.tools.enable(group_tools[i].group, group_tools[i].tool);
            }
            this.enable_draggable_tools();
            this.mode_switcher.enable();
            if (this.mode_switcher.mode === "map") {
                if (map.is_gis) {
                    global.tools.tools["geo_nodes"].$button.hide();
                    global.tools.tools["map_type"].$button.css("display", null);
                    svg.layer_hide("geolocation");
                } else {
                    global.tools.tools["geo_nodes"].$button.css("display", null);
                    global.tools.tools["map_type"].$button.hide();
                    svg.layer_show("geolocation");
                    this.interaction_passive_enable("map_move");
                }
            } else if (this.mode_switcher.mode === "graphics") {
                this.interaction_passive_enable("scene_move");
            } else if (this.mode_switcher.mode === "resources") {
                this.interaction_passive_enable("resources_move");
            }
            // AGPOC-6406: SITAC tools visible on log after joining collaboration
            if (BottomArea.switcher_left.mode === "tactics") {
                $("#command_map_right").show();
            }
            this.enable_all_features();
            if (global.storage.shared()) {
                if (!(global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_SPECIAL)) {
                    this.disable_special_features();
                }
                if (!(global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_WRITE)) {
                    this.disable_write_features();
                }
            }
        } else {
            for (let tool_name of single_tools) {
                global.tools.tools[tool_name].disable();
            }
            for (let i in group_tools) {
                global.tools.disable(group_tools[i].group, group_tools[i].tool);
            }
            this.disable_draggable_tools();
            this.mode_switcher.disable();
            $("#command_map_right").hide();
        }
    });

    global.event_bus.subscribe("storage.shared", () => {
        if (Menu.currently_opened !== null) {
            Menu.close(Menu.currently_opened);
        }
        this.mode_switcher.mode = "resources"; //default position AGPOC-2327
        $("#map_area button.add-media").removeAttr("disabled");
        $("#map_area button.add-gis").removeAttr("disabled");
        if (!(global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_SPECIAL)) {
            // User without special rights (observer + standard)
            this.disable_special_features();
        }
        if (!(global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_WRITE)) {
            // User without write rights (observer)
            this.disable_write_features();
        }
    });

    global.event_bus.subscribe("storage.unshared", () => {
        this.enable_all_features();
    });

    global.event_bus.subscribe("user.level.changed", () => {
        if (!global.storage.shared()) {
            return;
        }
        if (Menu.currently_opened !== null) {
            Menu.close(Menu.currently_opened);
        }
        this.enable_all_features();
        if (!(global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_SPECIAL)) {
            // User without special rights (observer + standard)
            this.mode_switcher.mode = "resources";
            this.disable_special_features();
        }
        if (!(global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_WRITE)) {
            // User without write rights (observer)
            this.disable_write_features();
        }
    });

    global.event_bus.subscribe("storage.space.changed", () => {
        this.mode_switcher.mode = "resources";
    });

    this.layer_hide = (name) => {
        svg.layer_hide(name);
    };

    this.layer_show = (name) => {
        svg.layer_show(name);
    };

    this.refresh_tools_state = () => {
        /*
        this has to be implemented elsewhere
        if (Menu.currently_opened !== null){
            this.disable_draggable_tools();
        } else {
            this.enable_draggable_tools();
        }
        */
    };

    this.change_context = async (new_context) => {
        log.debug("Scene.change_context:", new_context);
        // AGPOC-5359 : validating GIS-SITAC's name too soon genereates an uncaught exception
        // error indicates that context isn't initialized before creating the GIS object
        // the application should wait before context has changed before buttons add-gis and add-media are available
        $("#map_area button.add-media").attr("disabled", true);
        $("#map_area button.add-gis").attr("disabled", true);
        var old_context = context;
        context = new_context;
        if (new_context === old_context) {
            // AGPOC-4186 : buttons shouldn't be disabled for expert users when sharing space
            if (!global.storage.shared() || (global.storage.shared() && global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_SPECIAL)) {
                $("#map_area button.add-media").removeAttr("disabled");
                $("#map_area button.add-gis").removeAttr("disabled");
            }
            return;
        }
        if (old_context !== null) {
            objects.layer_reset_all();

            // save then delete all html players
            await floaters.save_context(old_context);
            floaters.remove_all();
        }
        await map.change_context(new_context);
        if (new_context !== null) {
            if (await new_context.is_empty()) {
                scene_mode_switcher.mode = "map";
            } else {
                scene_mode_switcher.mode = "resources";
            }
        }

        await objects.change_context(new_context);
        if (new_context !== null) {
            objects.layer_publish_all();
            svg.layer_insert("scene", "axis");
            svg.layer_insert("notes", "axis");
            svg.layer_insert("resources", "axis");
            svg.layer_insert("special", "axis");
            // AGPOC-5672 : do not display circle radius when not in graphics mode
            // code below needed because objects published after mode switcher changed
            $("#scene .disk .radius").css("display", "none");
            $("#scene .disk g.handle_resize_group").css("display", "none");
            $("#scene .disk g.distance_group").css("display", "none");
            $("#scene .line g.distance_group").css("display", "none");
        }

        // restore players in floaters
        await floaters.restore_context(new_context);

        // AGPOC-6403: change context code in Tracking was called before the object resource was present in the layer.
        // So, track function is called here, after the layer has been fully published.
        if (tracking.tracked !== undefined) {
            tracking.track(tracking.tracked);
        }

        if (global.storage.shared() && !(global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_WRITE)) {
            this.interaction_passive_disable("resources_move");
        } else {
            // AGPOC-3524: fixing possible lost of draggable interaction enable (appeared since default switcher mode is "resources")
            this.interaction_passive_enable("resources_move");
        }

        if (!global.storage.shared() || global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_SPECIAL) {
            // AGPOC-4186: re-enable buttons only if user has access
            $("#map_area button.add-media").removeAttr("disabled");
            $("#map_area button.add-gis").removeAttr("disabled");
        }
    };

    this.deinit = async () => {
        if (context === null) {
            return;
        }
        await map.deinit();
        context = null;
        await objects.deinit();
        objects.layer_reset_all();
    };

    this.drivers = {
        line: Line,
        cline: CurvedLine,
        disk: Disk,
        polygon: Polygon,
        arrow: Arrow,
        carrow: CurvedArrow,
        note: Note
    };

    interactions.add_draggable({
        name: "geonode_move",
        data: {
            node: null,
            initial_position: {x: 0, y: 0},
            trace: new Trace(objects.layers.geolocation.group),
            open_menu: true
        },
        target: {
            layer: objects.layers.geolocation,
            selector: "g.geonode.node"
        },
        enable: () => {},
        start: (data, node) => {
            log.debug("Scene.geonode_move.start");
            data.node = map.geonodes.get_by_node(node);
            data.open_menu = true;
            data.trace.init({
                position: data.node.position,
                handle: data.node.handle,
                color: map.grid.configuration.get("grid.color"),
                offset: (3 / 2) * global.app.scale.wb
            });
            shadow.affect(data.node.handle);
            data.initial_position.x = data.node.position.x;
            data.initial_position.y = data.node.position.y;
        },
        update: (data) => {
            data.node.move({x: d3.event.dx, y: d3.event.dy});
            data.trace.end(data.node.position);
            let distance = data.trace.distance();
            if (distance < 5) {
                return;
            }
            if (distance > 5) {
                // AGPOC-2981 : avoid menu open if the resource is replaced to original place
                data.open_menu = false;
            }
            global.resources.is_dragged(data.node.handle);
        },
        end: async (data) => {
            shadow.unaffect(data.node.handle);
            global.resources.not_dragged(data.node.handle);
            var distance = data.trace.distance();
            var EN = data.node.coordinates;
            if (distance < 5 && data.open_menu) {
                EN = await InputCoordinatesArea.interact(EN);
                if (EN !== null) {
                    data.node.set_coordinates(EN);
                }
                data.trace.remove();
                data.node = null;
                return;
            }

            if (distance < 75 / this.root_transform.k) {
                data.node.move_to(data.initial_position);
                data.node.apply();
                data.trace.remove();
                data.node = null;
                return;
            }

            var is_inside = global.tools.tools["geo_nodes"].is_inside(data.node.handle.node());
            if (is_inside) {
                data.node.remove();
                data.trace.remove();
                data.node = null;
                return;
            }

            var {x: ox, y: oy} = data.trace.offset();
            data.node.move({x: ox, y: oy});

            if (global.debug) {
                data.node.set_coordinates(EN);
                data.trace.remove();
                data.node = null;
                data.type = null;
                return;
            }

            EN = await InputCoordinatesArea.interact(EN);
            if (EN === null) {
                data.node.move_to(data.initial_position);
                data.trace.remove();
                data.node = null;
                data.type = null;
                return;
            }

            data.node.set_coordinates(EN);
            data.trace.remove();
            data.node = null;
            data.type = null;
        },
        disable: (data) => {
            data.node = null;
            data.type = null;
        }
    });

    interactions.add_group({
        name: "map_move",
        interactions: ["geonode_move"],
        enable: () => {},
        disable: () => {}
    });

    const grid_move_data = {
        position: {x: 0, y: 0},
        origin: {x: 0, y: 0},
        initial_origin: {x: 0, y: 0},
        square_width: 0,
        rate_cap: new RateCap(() => {
            map.grid.configuration.set("position.x", grid_move_data.position.x);
            map.grid.configuration.set("position.y", grid_move_data.position.y);
            map.grid.configuration.set("origin.x", grid_move_data.origin.x);
            map.grid.configuration.set("origin.y", grid_move_data.origin.y);
        }, 100)
    };

    interactions.add_draggable({
        name: "grid_move",
        data: grid_move_data,
        target: svg.root,
        enable: () => {
            view.disable_zoom();
        },
        start: (data) => {
            log.debug("Scene.grid_move.start");
            map.grid.ignore_axis = true; // do not draw axis for drawing performance
            svg.layer_hide("axis");
            svg.layer_hide("sub_axis");
            data.position = map.grid.configuration.get("position");
            data.origin = map.grid.configuration.get("origin");
            data.square_width = map.grid.configuration.get("square.width");
            data.initial_origin.y = map.grid.configuration.get("initial_origin.y");
            data.initial_origin.x = map.grid.configuration.get("initial_origin.x");
            log.debug("Scene.grid_move.start data = ", data);
        },
        update: (data) => {
            log.debug("Scene.grid_move.update in", {data});
            let ref_pos = map.grid.configuration.get("frame.reference.position");
            log.debug("Scene.grid_move.update position = ", {ref_pos});
            let {dx, dy} = this.transform_relative_coordinates(d3.event);
            log.debug("Scene.grid_move.update dx, dy", {dx, dy});
            data.position.x += dx;
            data.position.y += dy;
            data.x_min = map.grid.configuration.get("axis.x.min");
            data.y_min = map.grid.configuration.get("axis.y.min");

            if (map.grid.is_extended === true) {
                // if GIS, origin has same translation
                data.origin.x += dx;
                data.origin.y += dy;
            } else {
                // cas reference EAST
                if (ref_pos >= DIRECTION_SOUTH_EAST) {
                    // includes DIRECTION_NORTH_EAST
                    if (data.x_min === 0 && data.origin.x + dx <= map.width) {
                        data.origin.x += dx;
                    } else {
                        data.origin.x = data.initial_origin.x + Math.abs(((data.initial_origin.x - data.position.x) / data.square_width) % 1) * data.square_width - data.square_width;
                    }
                } else {
                    // DIRECTION_NORTH_WEST and DIRECTION_SOUTH_WEST
                    // reference WEST
                    // standard and GIGN grids
                    if (data.position.x < 0) {
                        data.origin.x = data.initial_origin.x + data.square_width - Math.abs((data.position.x / data.square_width) % 1) * data.square_width;
                    } else {
                        data.origin.x = data.position.x;
                    }
                }
                // cas reference SOUTH (GIGN)
                if (ref_pos >= 1 && ref_pos <= 2) {
                    // DIRECTION_SOUTH_WEST and DIRECTION_SOUTH_EAST
                    // GIGN grid
                    if (data.origin.y + dy <= map.height && data.y_min === 0) {
                        data.origin.y += dy;
                    } else {
                        data.origin.y = data.initial_origin.y - Math.abs(((data.initial_origin.y - data.position.y) / data.square_width) % 1) * data.square_width;
                    }
                } else {
                    //DIRECTION_NORTH_WEST and DIRECTION_NORTH_EAST
                    // reference NORTH
                    // standard grid
                    if (data.position.y < 0) {
                        data.origin.y = data.initial_origin.y + data.square_width - Math.abs((data.position.y / data.square_width) % 1) * data.square_width;
                    } else {
                        data.origin.y = data.position.y;
                    }
                }
            }
            log.debug("Scene.grid_move.update data = ", data);
            log.debug("Scene.grid_move.update data.origin = ", data.origin);
            data.rate_cap.poll();
        },
        end: () => {
            log.debug("Scene.grid_move.end");
            map.grid.ignore_axis = false;
            map.grid.init_axis(); // re-init axis range fo picture
            map.common_grid_save_after_move();
            map.grid.draw();
            map.grid.apply_to_all_desktops(); // apply new grid settings to all desktops
            global.tools.tool_unselect("grid_move"); //triggers interactions disable
        },
        disable: () => {}
    });

    interactions.add_draggable({
        name: "grid_resize",
        data: {
            initial_position: null,
            grid_origin: null,
            square_width: null,
            grid_initial_position: null,
            resized_grid: null,
            initial_distance: null
        },
        target: svg.root,
        enable: () => {
            log.debug("Scene.grid_resize.enable");
            view.disable_zoom();
        },
        start: (data) => {
            log.debug("Scene.grid_resize.start");
            map.grid.ignore_axis = true; // do not draw axis for drawing performance
            svg.layer_hide("axis");
            svg.layer_hide("sub_axis");

            data.initial_distance = map.grid.common_distance();
            data.square_width = map.grid.configuration.get("square.width");

            let {x: x1, y: y1} = this.transform_coordinates(d3.event, {ox: 0, oy: 0});
            data.initial_position = {x: x1, y: y1};
            let {x, y} = map.grid.configuration.get("position");
            data.grid_initial_position = {x, y};

            data.grid_origin = {
                x: map.grid.configuration.get("origin.x"),
                y: map.grid.configuration.get("origin.y")
            };
            if (map.grid.is_extended === false) {
                data.resized_grid = {
                    width: map.grid.configuration.get("resized_grid.width"),
                    height: map.grid.configuration.get("resized_grid.height")
                };
            } else {
                data.resized_grid = {
                    width: map.grid.configuration.get("grid.common_size.width"),
                    height: map.grid.configuration.get("grid.common_size.height")
                };
            }
        },
        update: (data) => {
            let direction = map.grid.configuration.get("frame.reference.position");
            log.debug("Scene.grid_resize.update in direction", {direction});
            let {x: x2, y: y2} = this.transform_coordinates(d3.event, {ox: 0, oy: 0});
            let {x: x1, y: y1} = data.initial_position;
            let coeff = 0;
            let {x: x0, y: y0} = data.grid_origin;

            // With pythagore computes the ratio between the current distance to (0,0) and  the initial distance to (0,0)
            coeff = Math.sqrt(Math.pow(x2 - x0, 2) + Math.pow(y2 - y0, 2)) / Math.sqrt(Math.pow(x1 - x0, 2) + Math.pow(y1 - y0, 2));
            log.debug("Scene.grid_resize.update", {x0, y0, x1, y1, x2, y2, coeff});

            if (map.grid.is_extended === true) {
                // GIS or geo-localized picture
                if (direction == DIRECTION_NORTH_WEST) {
                    // standard grid
                    let px = (data.grid_initial_position.x - x0) * coeff + x0;
                    let py = (data.grid_initial_position.y - y0) * coeff + y0;
                    log.debug("Scene.grid_resize.update px,py = ", {px, py});
                    map.grid.configuration.set("position.x", px);
                    map.grid.configuration.set("position.y", py);
                } else if (direction == DIRECTION_SOUTH_WEST) {
                    let px = (data.grid_initial_position.x - x0) * coeff + x0;
                    let py = (data.grid_initial_position.y - y0) * coeff + y0;
                    log.debug("Scene.grid_resize.update px,py = ", {px, py});
                    map.grid.configuration.set("position.x", px);
                    map.grid.configuration.set("position.y", py);

                    map.grid.configuration.set("resized_grid.width", data.resized_grid.width * coeff);
                    map.grid.configuration.set("resized_grid.height", data.resized_grid.height * coeff);
                }
            } else {
                // basic picture

                if (direction >= DIRECTION_SOUTH_EAST) {
                    // cas reference EAST
                    let px = (data.grid_initial_position.x - x0) * coeff + x0;
                    log.debug("Scene.grid_resize.update px = ", {px});
                    map.grid.configuration.set("position.x", px);
                    map.grid.configuration.set("resized_grid.width", data.resized_grid.width * coeff);
                } else {
                    // for standard and GIGN grids
                    let px = (data.grid_initial_position.x - x0) * coeff + x0;
                    log.debug("Scene.grid_resize.update px = ", {px});
                    map.grid.configuration.set("position.x", px);
                }

                if (
                    direction >= DIRECTION_SOUTH_WEST && // cas reference SOUTH (GIGN)
                    direction <= DIRECTION_SOUTH_EAST
                ) {
                    // GIGN grid
                    let py = (data.grid_initial_position.y - y0) * coeff + y0;
                    log.debug("Scene.grid_resize.update py = ", {py});
                    map.grid.configuration.set("position.y", py);
                    map.grid.configuration.set("resized_grid.height", data.resized_grid.height * coeff);
                } else {
                    // standard grid
                    let py = (data.grid_initial_position.y - y0) * coeff + y0;
                    log.debug("Scene.grid_resize.update py = ", {py});
                    map.grid.configuration.set("position.y", py);
                }
            }
            map.grid.frame.resizing_distance = data.initial_distance * coeff;
            // increase the square width with the computed coeff
            map.grid.square_size_update(data.square_width * coeff);
        },
        end: () => {
            log.debug("Scene.grid_resize.end");
            map.grid.frame.resizing_distance = undefined;
            map.grid.ignore_axis = false;
            map.common_grid_save_after_resize();
            map.grid.init_axis(); // re-init axis range fo picture
            map.grid.draw();
            map.grid.apply_to_all_desktops(); // apply new grid settings to all desktops
            global.tools.tool_unselect("grid_resize"); //triggers interactions disable
        },
        disable: () => {}
    });

    interactions.add_draggable({
        name: "drop_media",
        data: {
            active_attachment: null
        },
        target: svg.root,
        enable: () => {
            view.disable_zoom();
        },
        start: (data) => {
            log.debug("Scene.drop_media.start");
            if (global.medias.selected === null) {
                return;
            }
            data.active_attachment = new Attachment(objects.layers.resources.group, global.resources.category["_placeholder"]["media"].cloneNode(true), global.medias.selected);
            global.medias.selected = null;
            let {x, y} = this.transform_coordinates(d3.event, {
                ox: 100,
                oy: 100
            });
            data.active_attachment.draw();
            data.active_attachment.update(x, y);
        },
        update: (data) => {
            let {x, y} = this.transform_coordinates(d3.event, {
                ox: 100,
                oy: 100
            });
            /*
            if (x >= svg.width || y >= svg.height || x <= 0 || y <= 0) {
                return;
            }
            */
            data.active_attachment.update(x, y);
        },
        end: (data) => {
            objects.meta_create("resources", data.active_attachment);
            interactions.disable();
            global.tools.group_unselect("graphics_tools", "media");
        },
        disable: (data) => {
            data.active_attachment = null;
        }
    });

    interactions.add_draggable({
        name: "object_move",
        data: {
            active_object: null,
            trace: new Trace(objects.layers.scene.group),
            open_menu: true
        },
        target: {
            layer: objects.layers.scene,
            selector: "g.node"
        },
        enable: () => {},
        disable: (data) => {
            data.active_object = null;
        },
        filter: () => {
            return d3.event.target.getAttribute("class") !== null && d3.event.target.getAttribute("class").indexOf("object_handle") > -1;
        },
        start: (data, node) => {
            log.debug("Scene.interactions.object_move.start");
            data.active_object = objects.get_by_node("scene", node.parentNode);
            // By hidding the svg temporary we hide the SVGShadow created with it
            if (data.active_object.handle.select("g.handle_resize_group").size() > 0) {
                data.active_object.handle.select("g.handle_resize_group").attr("visibility", "hidden");
            }
            data.active_object.top();
            data.open_menu = true;
            shadow.unaffect(data.active_object.handle); // reinitialise shadow in order to get the right trace in case of selected object
            data.trace.init({
                position: data.active_object.center(),
                handle: data.active_object.handle,
                color: map.grid.configuration.get("grid.color")
            });
            // Show the svg again now that the SVGShadow is hidden and show distances too
            if (data.active_object.handle.select("g.handle_resize_group").size() > 0) {
                data.active_object.handle.select("g.handle_resize_group").attr("visibility", "visible");
            }
            if (data.active_object.handle.select("g.distance_group").size() > 0 && global.storage.selected_context.projection.project !== null) {
                style.rule_set(
                    `${data.active_object.handle.property("id")}_visibility`,
                    `
                #${data.active_object.handle.property("id")} g.distance_group {visibility: visible}
            `
                );
            }
        },
        update: (data) => {
            data.active_object.move({x: d3.event.dx, y: d3.event.dy});
            data.trace.end(data.active_object.center());
            if (data.trace.distance() > 5 / this.root_transform.k) {
                // AGPOC-2981 : avoid menu open if the resource is replaced to original place
                data.open_menu = false;
            }
        },
        end: (data) => {
            if (data.trace.distance() < 5 / this.root_transform.k && data.open_menu) {
                GraphicsUpdateArea.select(data.active_object.handle.node());
                focus_graphicsUpdateArea = data.active_object; // Keep in memory the element
                data.active_object = null;
                data.trace.remove();
                return;
            }
            // Check if the update area is open and the modified element is the focused one
            if (Menu.currently_opened === "graphics_update_area" && focus_graphicsUpdateArea == data.active_object) {
                shadow.affect(data.active_object.handle);
            }
            if (data.active_object.handle.select("g.distance_group").size() > 0) {
                style.rule_set(
                    `${data.active_object.handle.property("id")}_visibility`,
                    `
                #${data.active_object.handle.property("id")} g.distance_group {visibility: hidden}
            `
                );
            }

            if (global.tools.tools["object_remove"].is_inside(d3.event.sourceEvent.target)) {
                objects_stack.push({layer: "scene", object: data.active_object});
                if (objects_stack.length > 16) {
                    objects_stack.splice(-1, 1);
                }
                objects.tx_delete("scene", data.active_object);
            } else {
                objects.tx_update("scene", data.active_object);
            }
            data.trace.remove();
            if (Menu.currently_opened === "graphics_update_area") {
                if (data.active_object.handle.select("g.distance_group").size() > 0 && focus_graphicsUpdateArea == data.active_object && global.storage.selected_context.projection.project !== null) {
                    style.rule_set(
                        `${data.active_object.handle.property("id")}_visibility`,
                        `
                    #${data.active_object.handle.property("id")} g.distance_group {visibility: visible}
                `
                    );
                }
            }
            data.active_object = null;
        }
    });

    // add to Interactions the function disk_resize which is mapped to the handle inside the target.selection
    interactions.add_draggable({
        name: "disk_resize",
        data: {
            active_object: null // Contains the node of the selected object
        },
        // Focus on a specific HTML layer
        target: {
            layer: objects.layers.scene,
            selector: "g.node.disk g.handle_resize_group"
        },
        enable: () => {},
        disable: (data) => {
            data.active_object = null;
        },
        // Checking if the user is interacting with a object_handle
        filter: () => {
            log.debug(d3.event.target.getAttribute("class"));
            return d3.event.target.getAttribute("class") !== null && d3.event.target.getAttribute("class").indexOf("object_handle") > -1;
        },
        start: (data, node) => {
            log.debug("Scene.disk_resize.start");
            data.active_object = objects.get_by_node("scene", node.parentNode.parentNode);
            global.active_object = data.active_object;
            log.debug("data.active_object: ", data.active_object);
            log.debug("data.active_object.handle: ", data.active_object.handle);
            data.active_object.top();
            shadow.unaffect(data.active_object.handle); // reinitialise shadow in order to get the right trace in case of selected object
            if (data.active_object.handle.select("g.distance_group").size() > 0 && global.storage.selected_context.projection.project !== null) {
                style.rule_set(
                    `${data.active_object.handle.property("id")}_visibility`,
                    `
                #${data.active_object.handle.property("id")} g.distance_group {visibility: visible}
            `
                );
            }
        },
        // Update in real time the Disk, showing AND updating it
        update: (data) => {
            let origin_x = data.active_object.handle.select("circle").attr("cx");
            let origin_y = data.active_object.handle.select("circle").attr("cy");
            // Compute new radius using the position in D3 of the cursor. Coordinates will be used to update the Disk dynamically.
            let new_radius = Math.sqrt(Math.pow(d3.event.x - parseFloat(origin_x), 2) + Math.pow(d3.event.y - parseFloat(origin_y), 2));
            data.active_object.handle.select("circle").attr("r", new_radius);

            // Preparing coordinates of arrow
            const a = 60,
                b = 40,
                x0 = d3.event.x,
                y0 = d3.event.y;
            let handle_resize_points = [
                [x0 + a, y0],
                [x0, y0 - b],
                [x0 - a, y0],
                [x0, y0 + b]
            ];

            let dx = x0 - origin_x,
                dy = y0 - origin_y;
            // Update the visible radius
            data.active_object.handle.select("path.radius").attr("d", `M${origin_x},${origin_y},${x0},${y0}`);
            let angle = get_angle(dx, dy);
            // Following lines are responsible of positionning the handle and the arrow and managing the rotation
            data.active_object.handle.select("g.handle_resize_group circle").attr("cx", x0);
            data.active_object.handle.select("g.handle_resize_group circle").attr("cy", y0);
            data.active_object.handle.select("g.handle_resize_group").attr("transform-origin", `${x0}px ${y0}px`);
            data.active_object.handle.select("g.handle_resize_group polygon").attr("transform-origin", `${x0}px ${y0}px`);
            data.active_object.handle.select("g.handle_resize_group").attr("transform", `rotate(${angle})`);
            data.active_object.handle.select("g.handle_resize_group polygon").attr("points", handle_resize_points);

            let computed_distance = global.view.get_distance_meter([origin_x, origin_y], [x0, y0]);
            if (computed_distance !== undefined) { // i.e. if a projection is defined for the view
                data.active_object.handle.select("g.distance_group g g text").html(format_distance(computed_distance));
            }
            // Check if there is a projection, this projection is essential for mapping pixel to geo-coordinates.
            if (global.storage.selected_context.projection.project !== null) {
                // We will place the distance on the middle of the radius
                let middle_radius = {
                    x: (x0 + parseFloat(origin_x)) / 2,
                    y: (parseFloat(origin_y) + y0) / 2
                };

                // Compute necessary space for the rect to contain the text
                var {width: w1, height: h1} = data.active_object.handle.select("g.distance_group g g text").node().getBBox();
                let text_pos_x = middle_radius.x - w1 / 2;
                let text_pos_y = middle_radius.y + h1 / 3; // Division here was found manually, it's suppose to work fine

                data.active_object.handle.select("g.distance_group g g text").attr("x", text_pos_x);
                data.active_object.handle.select("g.distance_group g g text").attr("y", text_pos_y);
                // adding padding
                w1 += 18;
                h1 += 6;
                let rect_pos_x = middle_radius.x - w1 / 2;
                let rect_pos_y = middle_radius.y - h1 / 2;

                data.active_object.handle.select("g.distance_group g g rect").attr("x", rect_pos_x);
                data.active_object.handle.select("g.distance_group g g rect").attr("y", rect_pos_y);
                data.active_object.handle.select("g.distance_group g g rect").attr("width", w1);
                data.active_object.handle.select("g.distance_group g g rect").attr("height", h1);
                // Transform-origin is necessary here for the scale on zoom and dezoom
                data.active_object.handle.select("g.distance_group .distance").attr("transform-origin", `${middle_radius.x}px ${middle_radius.y}px`);
            } else {
                //grid scale not visible if sitac is not georeferenced
                if (data.active_object.handle.select("g.distance_group").size() > 0) {
                    style.rule_set(
                        `${data.active_object.handle.property("id")}_visibility`,
                        `
                    #${data.active_object.handle.property("id")} g.distance_group {visibility: hidden}
                `
                    );
                }
            }
        },
        end: (data) => {
            if (data.active_object.handle.select("g.distance_group").size() > 0) {
                style.rule_set(
                    `${data.active_object.handle.property("id")}_visibility`,
                    `
                #${data.active_object.handle.property("id")} g.distance_group {visibility: hidden}
            `
                );
            }
            if (Menu.currently_opened === "graphics_update_area") {
                if (data.active_object.handle.select("g.distance_group").size() > 0 && focus_graphicsUpdateArea == data.active_object && global.storage.selected_context.projection.project !== null) {
                    style.rule_set(
                        `${data.active_object.handle.property("id")}_visibility`,
                        `
                    #${data.active_object.handle.property("id")} g.distance_group {visibility: visible}
                `
                    );
                }
            }
            objects.tx_update("scene", data.active_object); // Push modification to the share
            data.active_object = null;
        }
    });

    /**
     * @brief Handle the moving of a note.
     * Display the note menu if moving is short (<5)
     */
    interactions.add_draggable({
        name: "note_move",
        data: {
            active_object: null,
            trace: new Trace(objects.layers.notes.group),
            open_menu: true
        },
        target: {
            layer: objects.layers.notes,
            selector: "g.node"
        },
        enable: () => {},
        disable: (data) => {
            data.active_object = null;
        },
        filter: () => {
            return d3.event.target.getAttribute("class") !== null && d3.event.target.getAttribute("class").indexOf("object_handle") > -1;
        },
        start: (data, node) => {
            log.debug("Scene.note_move.start");
            data.active_object = objects.get_by_node("notes", node.parentNode);
            data.active_object.top();
            data.open_menu = true;
            shadow.unaffect(data.active_object.handle); // reinitialise shadow in order to get the right trace in case of selected object
            data.trace.init({
                position: data.active_object.center(),
                handle: data.active_object.handle,
                color: map.grid.configuration.get("grid.color")
            });
        },
        update: (data) => {
            data.active_object.move({x: d3.event.dx, y: d3.event.dy});
            if (data.trace.distance() > 5 / this.root_transform.k) {
                // AGPOC-2981 : avoid menu open if the resource is replaced to original place
                data.open_menu = false;
            }
            data.trace.end(data.active_object.center());
        },
        end: (data) => {
            if (data.trace.distance() < 5 / this.root_transform.k && data.open_menu) {
                NotesUpdateArea.select(data.active_object.handle.node());
                data.active_object = null;
                data.trace.remove();
                return;
            }
            if (Menu.currently_opened === "notes_update_area") {
                shadow.affect(data.active_object.handle);
            }
            if (global.tools.tools["object_remove"].is_inside(d3.event.sourceEvent.target)) {
                objects_stack.push({layer: "notes", object: data.active_object});
                if (objects_stack.length > 16) {
                    objects_stack.splice(-1, 1);
                }
                objects.tx_delete("notes", data.active_object);
            } else {
                objects.tx_update("notes", data.active_object);
            }
            data.active_object = null;
            data.trace.remove();
        }
    });

    // when in Graphics Mode, add following interactions
    interactions.add_group({
        name: "scene_move",
        interactions: ["object_move", "note_move", "disk_resize"],
        // when entering Graphics mode, change rule_set so handles/visual elements are displayed. Vice-versa when exiting mode.
        enable: () => {
            ObjectHandle.show();
            style.rule_set(
                "scene_arrow&radius_display",
                `
                .disk .radius {
                    display : block !important;
                }
                .disk g {
                    display : block !important;
                }
                .distance_group {
                    display : block !important;
                }
            `
            );
        },
        disable: () => {
            ObjectHandle.hide();
            style.rule_set(
                "scene_arrow&radius_display",
                `
                .disk .radius {
                    display : none !important;
                }
                .disk g {
                    display : none !important;
                }
                .distance_group {
                    display : none !important;
                }
            `
            );
        }
    });

    /**
     * @brief Handle the moving of a resource.
     * Display the resource menu if moving is short (<5)
     * Come back to initial position if moving is medium (<75)
     * Propose to release resource if it is attached to a user
     */
    interactions.add_draggable({
        name: "resource_move",
        data: {
            active_object: null,
            initial_position: {x: 0, y: 0},
            trace: new Trace(objects.layers.resources.group),
            open_menu: true,
            timer: null,
            clickedOnce: false
        },
        target: {
            layer: objects.layers.resources,
            selector: "g.resource.node"
        },
        enable: () => {},
        disable: () => {},
        start: (data, node) => {
            log.debug("Scene.resource_move.start");
            data.active_object = objects.get_by_node("resources", node.parentNode);
            data.active_object.top();
            shadow.unaffect(data.active_object.handle);

            // show the picture if it is hidden because of the zoom
            global.resources.show_picture(data.active_object);

            // finding the center of the circle and instead of the whole resource
            let center_position = {
                x: data.active_object.node_transform.x + 100,
                y: data.active_object.node_transform.y + 100
            };
            data.trace.init({
                position: center_position,
                handle: data.active_object.handle,
                color: map.grid.configuration.get("grid.color"),
                offset: (3 / 2) * global.app.scale.wb
            });
            data.initial_position.x = data.active_object.node_transform.x;
            data.initial_position.y = data.active_object.node_transform.y;
            data.open_menu = true;
        },
        update: (data) => {
            data.active_object.move({x: d3.event.dx, y: d3.event.dy});

            let center_position = {
                x: data.active_object.node_transform.x + 100,
                y: data.active_object.node_transform.y + 100
            };

            data.trace.end(center_position);
            let distance = data.trace.distance();
            let threshold = 5 / this.root_transform.k;
            //log.debug(`Scene resource_move.end distance = ${distance} < 5, k= ${this.root_transform.k}, threshold = ${threshold}`);
            if (distance < threshold) {
                return;
            }
            if (distance > threshold) {
                // AGPOC-2981 : avoid menu open if the resource is replaced to original place
                data.open_menu = false;
            }
            global.resources.is_dragged(data.active_object.handle);
        },
        end: async (data) => {
            if (data.active_object === null) {
                return;
            }
            global.resources.not_dragged(data.active_object.handle);
            let distance = data.trace.distance();
            if (distance < 5 / this.root_transform.k && data.open_menu && data.active_object.node.attr("data-msisdn") === null) {
                if (data.clickedOnce) {
                    data.clickedOnce = false;
                    clearTimeout(data.timer);
                    $(data.active_object.node.node()).trigger("dblclick");
                    data.active_object = null;
                    Menu.close("resources_edit_area"); // in order to not have unlogical shadow
                } else {
                    data.timer = setTimeout(function () {
                        data.clickedOnce = false;
                        ResourcesEditorArea.select(data.active_object.handle.node());
                        data.active_object = null;
                    }, 300);
                    data.clickedOnce = true;
                }
                data.trace.remove();
                return;
            }
            // Agnet resource : disable resource move
            if (data.active_object.node.attr("data-msisdn") !== null) {
                if (distance > 0) {
                    data.active_object.move_to(data.initial_position);
                } else {
                    ResourcesEditorArea.select(data.active_object.handle.node());
                }
                data.active_object = null;
                data.trace.remove();
                return;
            }

            //shadow.unaffect(data.active_object.handle);
            if (distance < 75 / this.root_transform.k) {
                data.active_object.move_to(data.initial_position);
                data.active_object = null;
                data.trace.remove();
                return;
            }
            if (Menu.currently_opened === "resources_edit_area") {
                shadow.affect(data.active_object.handle);
            }
            let is_inside = global.tools.tools["resource_remove"].is_inside(data.active_object.handle.select("g.figure").node());
            if (is_inside) {
                resources_stack.push(data.active_object);
                objects.tx_delete("resources", data.active_object);
                data.active_object = null;
                data.trace.remove();
                return;
            }

            if (data.active_object.node.attr("data-user") !== null) {
                let confirmation = await ConfirmationArea.interact({
                    title: global.internationalization.text("resource.Release"),
                    tip1: global.internationalization.text("resource.Release-resource")
                });
                if (!confirmation) {
                    data.active_object.move_to(data.initial_position);
                    objects.tx_update("resources", data.active_object);
                    data.active_object = null;
                    data.trace.remove();
                    return;
                }
                global.resources.user_name(data.active_object.handle, "");
                data.active_object.node.attr("data-user", null);
            }
            let {x: ox, y: oy} = data.trace.offset();
            data.active_object.move({x: ox, y: oy});

            objects.tx_update("resources", data.active_object);
            data.active_object = null;
            data.trace.remove();
        }
    });

    /**
     * @brief Handle the moving of an attachment.
     * Display the attachment menu if moving is short (<5) and clicked once
     * Display the attachment if moving is short (<5) and clicked double
     * Come back to initial position if moving is medium (<75)
     */
    interactions.add_draggable({
        name: "attachment_move",
        data: {
            active_object: null,
            initial_position: {x: 0, y: 0},
            trace: new Trace(objects.layers.resources.group),
            clickedOnce: false,
            timer: null,
            open_menu: true
        },
        target: {
            layer: objects.layers.resources,
            selector: "g.attachment.node"
        },
        enable: () => {
            log.debug("Scene.attachment_move.enable");
        },
        start: (data, node) => {
            log.debug("Scene.attachment_move.start");
            data.active_object = objects.get_by_node("resources", node.parentNode);
            data.active_object.top();
            shadow.unaffect(data.active_object.handle);

            // finding the center of the circle and instead of the whole resource
            let center_position = {
                x: data.active_object.node_transform.x + 100,
                y: data.active_object.node_transform.y + 100
            };
            data.trace.init({
                position: center_position,
                handle: data.active_object.handle,
                color: map.grid.configuration.get("grid.color"),
                offset: (3 / 2) * global.app.scale.wb
            });
            data.initial_position.x = data.active_object.node_transform.x;
            data.initial_position.y = data.active_object.node_transform.y;
            data.open_menu = true;
        },
        update: (data) => {
            log.debug("Scene.attachment_move.update");
            data.active_object.move({x: d3.event.dx, y: d3.event.dy});

            let center_position = {
                x: data.active_object.node_transform.x + 100,
                y: data.active_object.node_transform.y + 100
            };

            data.trace.end(center_position);
            let distance = data.trace.distance();
            let threshold = 5 / this.root_transform.k;
            if (distance < threshold) {
                return;
            }
            if (distance > threshold) {
                // AGPOC-2981 : avoid menu open if the resource is replaced to original place
                data.open_menu = false;
            }
            global.resources.is_dragged(data.active_object.handle);
        },
        end: async (data) => {
            log.debug("Scene.attachment_move.end");
            if (data.active_object === null) {
                return;
            }
            let distance = data.trace.distance();
            if (distance < 5 / this.root_transform.k && data.open_menu) {
                if (data.clickedOnce) {
                    log.debug("Scene.attachment_move.end clickedOnce = true");
                    data.clickedOnce = false;
                    clearTimeout(data.timer);
                    $(data.active_object.node.node()).trigger("dblclick");
                    data.active_object = null;
                    Menu.close("attachments_edit_area"); // in order to not have unlogical shadow
                } else {
                    log.debug("Scene.attachment_move.end set timeout");
                    data.timer = setTimeout(function () {
                        data.clickedOnce = false;
                        AttachmentsEditorArea.select(data.active_object.handle.node());
                        data.active_object = null;
                    }, 300);
                    data.clickedOnce = true;
                }
                data.trace.remove();
                return;
            }
            if (distance < 75 / this.root_transform.k) {
                data.active_object.move_to(data.initial_position);
                data.active_object = null;
                data.trace.remove();
                return;
            }

            if (Menu.currently_opened === "attachments_edit_area") {
                shadow.affect(data.active_object.handle);
            }

            let is_inside = global.tools.tools["resource_remove"].is_inside(data.active_object.handle.select("g.figure").node());
            if (is_inside) {
                resources_stack.push(data.active_object);
                let media_id = data.active_object.node.attr("data-media");
                let media = await context.space.media_retrieve({local_id: media_id});
                medias_stack.push(media);
                objects.tx_delete("resources", data.active_object);
                let type = media.media_data.type.split("/")[0];
                if (type === "image") {
                    global.medias.unload_media({local_id: media_id});
                }
                data.active_object = null;
                data.media_object = null;
                data.trace.remove();
                return;
            }
            //let {x: cx1} = data.active_object.center("g.figure");
            //let {x: cx2} = data.active_object.center();
            let {x: ox, y: oy} = data.trace.offset();
            data.active_object.move({x: ox, y: oy});
            objects.tx_update("resources", data.active_object);
            data.active_object = null;
            data.trace.remove();
        },
        disable: (data) => {
            log.debug("Scene.attachment_move.disable");
            data.active_object = null;
        }
    });

    interactions.add_group({
        name: "resources_move",
        interactions: ["resource_move", "attachment_move"],
        enable: () => {},
        disable: () => {}
    });

    interactions.set_default({
        name: "zoom",
        enable: () => {
            view.enable_zoom();
        },
        disable: () => {
            view.disable_zoom();
        }
    });

    this.renderer = new Renderer(svg.root.node(), map);
    this.export_map = async () => {
        if (global.view.is_gis) {
            global.view.ol_driver.export_ongoing = false;
        }
        return this.renderer.render();
    };

    this.interaction_enable = (name) => {
        interactions.enable(name);
    };

    this.interaction_disable = (name) => {
        interactions.disable(name);
    };

    this.disable_draggable_tools = () => {
        for (let tool_name in global.tools.tools) {
            if (global.tools.tools[tool_name].is_draggable) {
                global.tools.tools[tool_name].disable();
            }
        }
    };

    this.enable_draggable_tools = () => {
        for (let tool_name in global.tools.tools) {
            if (global.tools.tools[tool_name].is_draggable) {
                global.tools.tools[tool_name].enable();
            }
        }
    };

    this.interaction_passive_enable = (name) => {
        interactions.passive_enable(name);
    };

    this.interaction_passive_disable = (name) => {
        interactions.passive_disable(name);
    };

    /**
     * Computes the coordinate distance between the mouse and the point {ox,oy}, cancelling the effect of the transformation {x,y,k},
     * i.e. reversing the effect of the transformation
     * @param {*} {x,y} in, mouse position
     * @param {*} {ox, oy} in, the distant point (ex, (0,0) is the top left of the picture)
     * @returns the distances {x,y} in pixel
     */
    this.transform_coordinates = function ({x, y}, {ox, oy}) {
        //log.debug("Scene.transform_coordinates in ", {x, y, ox, oy});
        //log.debug("Scene.transform_coordinates svg", svg.margin);
        //log.debug("Scene.transform_coordinates root_transform", this.root_transform);
        let dx = (x - svg.margin.x - this.root_transform.x - ox) / this.root_transform.k; /*+ ox*/
        let dy = (y - svg.margin.y - this.root_transform.y - oy) / this.root_transform.k; /*+ oy*/
        //log.debug("Scene.transform_coordinates return", {dx, dy});
        return {x: dx, y: dy};
    };

    /**
     * @brief Get the D3 Map coordinate of a screen pixel. Apply the revert transformation
     * @param {*} x
     * @param {*} y
     * @returns
     */
    this.transform_coordinates_3 = function ({x, y}) {
        //log.debug("Scene.transform_coordinates_3 in ", {x, y});
        let dx = (x - this.root_transform.x) / this.root_transform.k;
        let dy = (y - this.root_transform.y) / this.root_transform.k;
        //log.debug("Scene.transform_coordinates_3 return", {dx, dy});
        return {x: dx, y: dy};
    };

    this.transform_coordinates_inverse = function ({x, y}) {
        return {
            x: x * this.root_transform.k + svg.margin.x + this.root_transform.x,
            y: y * this.root_transform.k + svg.margin.y + this.root_transform.y
        };
    };

    this.transform_relative_coordinates = function ({dx, dy}) {
        return {
            dx: dx / this.root_transform.k,
            dy: dy / this.root_transform.k
        };
    };

    this.root_transform = new Transform({x: 0, y: 0, k: 1});
    this.root_transform.on_scale(({k}) => {
        log.debug("root_transform on scale {k} = ", {k});
        style.rule_set(
            "scene_scale_factor",
            `
            :root {
                --scale-factor: ${1 / k};
            }
        `
        );
        global.resources.scale_factor = 1 / k;
    });

    this.root_transform.on_change(({x, y, k}) => {
        log.debug("root_transform on change {x, y, k} = ", {x, y, k});
        objects.layer_transform_all({x, y, k});
    });
    for (let driver_name in this.drivers) {
        if (!("transform" in this.drivers[driver_name])) {
            continue;
        }
        this.root_transform.append_child(this.drivers[driver_name].transform);
    }
    this.root_transform.append_child(ObjectHandle.transform);
    this.root_transform.append_child(Placeholder.transform);
    this.root_transform.append_child(Trace.transform);
    this.root_transform.append_child(Placer.transform);
    this.root_transform.append_child(Indicator.transform);
    this.root_transform.append_child(shadow.transform);
    this.root_transform.append_child(map.transform);

    this.transform = (state) => {
        log.debug("transform state:", state);
        this.root_transform.state = state;
    };

    this.resize = function () {};

    const get_angle = (dx, dy) => {
        var angle;
        if (dx === 0) {
            if (dy < 0) {
                angle = -Math.PI / 2;
            } else {
                angle = Math.PI / 2;
            }
        } else if (dx > 0) {
            angle = Math.atan(dy / dx);
        } else if (dx < 0) {
            angle = Math.atan(dy / dx) - Math.PI;
        }
        angle = (angle / (2 * Math.PI)) * 360;
        return angle;
    };
};

global.app.bootstrap.register_module({
    name: "scene",
    deps: ["storage", "svg", "view", "resources", "default_configuration", "ui_bottom_area"],
    init: () => {
        global.scene = new Scene(global.view, global.svg);
        global.svg.object_add(global.scene);
        global.event_bus.subscribe("storage.context.changed", async ({old_context, new_context}) => {
            log.debug("on storage.context.changed:", {old_context, new_context});
            if (Menu.currently_opened !== null) {
                Menu.close(Menu.currently_opened);
            }
            if (new_context === old_context) {
                return;
            }
            if (old_context !== null && old_context.room !== null && new_context !== null) {
                let room = old_context.room.duplicate();
                room.desktop_index = new_context.index;
                await new_context.share(room);
            }
            await global.view.change_context(new_context);
            await global.scene.change_context(new_context);
        });
    }
});

global.app.bootstrap.register_module({
    name: "scene_tools",
    deps: ["tools", "scene"],
    init: () => {
        global.tools.register({
            name: "share",
            icon: "server",
            position: "#header_toolbar_left",
            classes: "le-bidon",
            handler: () => {}
        });
        global.tools.register({
            name: "room_settings",
            icon: "settings",
            position: "#header_toolbar_right",
            classes: "",
            handler: () => {
                Menu.display(["ui_settings_area"]);
            }
        });
        global.tools.register({
            name: "desktop_settings",
            icon: "tiroir",
            position: "#desktop_menu",
            classes: "",
            handler: () => {
                if (global.storage.shared() && !(global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_SPECIAL)) {
                    return;
                }
                Menu.display(["desktop_settings_area"]);
            }
        });
    }
});
